import React, { useState, useEffect } from 'react';
import { todaysOrdersAPI } from '../services/api';
import { 
  Calendar, 
  Search, 
  Filter, 
  RefreshCw,
  Package,
  Clock,
  MapPin,
  Phone,
  DollarSign,
  TrendingUp
} from 'lucide-react';
import toast from 'react-hot-toast';

const TodaysOrders = () => {
  const [activeTab, setActiveTab] = useState('today');
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState('none');
  const [filters, setFilters] = useState({
    timeSlot: '',
    orderType: '',
    status: '',
    search: ''
  });

  useEffect(() => {
    fetchOrders();
  }, [activeTab, filters]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      let response;
      
      const params = {};
      if (filters.timeSlot) params.timeSlot = filters.timeSlot;
      if (filters.orderType) params.orderType = filters.orderType;
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;

      switch (activeTab) {
        case 'today':
          response = await todaysOrdersAPI.getToday(params);
          break;
        case 'tomorrow':
          response = await todaysOrdersAPI.getTomorrow(params);
          break;
        case 'weekly':
          response = await todaysOrdersAPI.getWeekly(params);
          break;
        default:
          response = await todaysOrdersAPI.getToday(params);
      }

      // Ensure orders is always an array with defensive checks
      const ordersData = response?.data?.data?.orders || [];
      const summaryData = response?.data?.data?.summary || null;
      
      console.log('📦 Orders fetched:', { count: ordersData.length, summary: summaryData });
      
      setOrders(ordersData);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
      setOrders([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (uuid, newStatus) => {
    try {
      await todaysOrdersAPI.updateStatus(uuid, newStatus);
      toast.success('Order status updated successfully');
      fetchOrders();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update order status');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      timeSlot: '',
      orderType: '',
      status: '',
      search: ''
    });
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
    preparing: 'bg-purple-100 text-purple-800 border-purple-300',
    out_for_delivery: 'bg-orange-100 text-orange-800 border-orange-300',
    delivered: 'bg-green-100 text-green-800 border-green-300',
    cancelled: 'bg-red-100 text-red-800 border-red-300'
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Orders Management</h1>
          <p className="text-gray-600 mt-1">Manage today's, tomorrow's, and weekly orders</p>
        </div>
        <button
          onClick={fetchOrders}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('today')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'today'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Today's Orders
              </div>
            </button>
            <button
              onClick={() => setActiveTab('tomorrow')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'tomorrow'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Tomorrow's Orders
              </div>
            </button>
            <button
              onClick={() => setActiveTab('weekly')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'weekly'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Weekly Orders
              </div>
            </button>
          </nav>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="p-6 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-8 gap-3">
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-xs text-gray-600 font-medium">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{summary.totalOrders || 0}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-xs text-gray-600 font-medium">Revenue</p>
                <p className="text-xl font-bold text-green-600">₹{((summary.totalRevenue || 0) / 1000).toFixed(1)}K</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500">
                <p className="text-xs text-gray-600 font-medium">🛒 Normal</p>
                <p className="text-2xl font-bold text-blue-600">{summary.normalOrders || 0}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-purple-500">
                <p className="text-xs text-gray-600 font-medium">📦 Subscription</p>
                <p className="text-2xl font-bold text-purple-600">{summary.subscriptionOrders || 0}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-xs text-gray-600 font-medium">⏳ Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{summary.pendingOrders || 0}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-xs text-gray-600 font-medium">✓ Confirmed</p>
                <p className="text-2xl font-bold text-blue-500">{summary.confirmedOrders || 0}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-xs text-gray-600 font-medium">👨‍🍳 Preparing</p>
                <p className="text-2xl font-bold text-orange-600">{summary.preparingOrders || 0}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-xs text-gray-600 font-medium">✔️ Delivered</p>
                <p className="text-2xl font-bold text-green-600">{summary.deliveredOrders || 0}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="p-6 bg-white border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by customer, phone, order#..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Time Slot Filter */}
            <select
              value={filters.timeSlot}
              onChange={(e) => handleFilterChange('timeSlot', e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Time Slots</option>
              <option value="morning">Morning</option>
              <option value="evening">Evening</option>
              <option value="anytime">Anytime</option>
            </select>

            {/* Order Type Filter */}
            <select
              value={filters.orderType}
              onChange={(e) => handleFilterChange('orderType', e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Order Types</option>
              <option value="normal">Normal Orders</option>
              <option value="subscription">Subscription Orders</option>
            </select>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="preparing">Preparing</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Clear Filters */}
          {(filters.search || filters.timeSlot || filters.orderType || filters.status) && (
            <button
              onClick={clearFilters}
              className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear all filters
            </button>
          )}
        </div>

        {/* Grouping Controls */}
        {orders.length > 0 && (
          <div className="p-6 bg-gray-50 border-b border-gray-200 flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Group Orders By:</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="none">No Grouping</option>
              <option value="type">Order Type</option>
              <option value="pincode">Delivery Area (Pincode)</option>
              <option value="timeSlot">Time Slot</option>
              <option value="status">Order Status</option>
            </select>
          </div>
        )}

        {/* Orders List */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No orders found</p>
              <p className="text-gray-500 text-sm mt-2">Try adjusting your filters</p>
            </div>
          ) : groupBy === 'none' ? (
            <div className="space-y-4">
              {orders.map((order) => (
                <OrderCard
                  key={order.uuid}
                  order={order}
                  onStatusUpdate={handleStatusUpdate}
                  statusColors={statusColors}
                />
              ))}
            </div>
          ) : (
            <GroupedOrdersList
              orders={orders}
              groupBy={groupBy}
              onStatusUpdate={handleStatusUpdate}
              statusColors={statusColors}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Order Card Component
const OrderCard = ({ order, onStatusUpdate, statusColors }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const canDeliver = order.status === 'confirmed' || order.status === 'preparing';

  return (
    <div className={`border-l-4 border rounded-lg hover:shadow-md transition-shadow ${
      order.orderType === 'subscription' 
        ? 'border-l-purple-500 bg-purple-50' 
        : 'border-l-blue-500 bg-blue-50'
    }`}>
      {/* Order Header */}
      <div className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-lg font-semibold text-gray-900">{order.orderNumber}</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                order.orderType === 'subscription' 
                  ? 'bg-purple-100 text-purple-800 border border-purple-300' 
                  : 'bg-blue-100 text-blue-800 border border-blue-300'
              }`}>
                {order.orderType === 'subscription' ? '📦 Subscription' : '🛒 Normal'}
              </span>
              {canDeliver && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                  ✓ Ready to Deliver
                </span>
              )}
            </div>
            
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
              <div className="flex items-center gap-2">
                <span className="font-medium">👤 {order.customerName}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <span>{order.customerPhone}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span className="capitalize font-medium">{order.deliveryTimeSlot} Slot</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span className="font-medium">{order.deliveryPincode}</span>
              </div>
            </div>

            {order.orderType === 'subscription' && (
              <div className="mt-2 p-2 bg-white rounded border border-purple-200">
                <p className="text-xs text-gray-600">
                  <span className="font-semibold text-purple-700">📋 {order.subscriptionPlanName}</span>
                  {order.companyName && <span className="text-gray-600"> • {order.companyName}</span>}
                </p>
              </div>
            )}
          </div>

          <div className="text-right ml-4">
            <p className="text-2xl font-bold text-gray-900">₹{(order.totalAmount || 0).toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1 font-medium">{(order.paymentMethod || 'N/A').toUpperCase()}</p>
            <p className={`text-xs mt-1 font-semibold ${
              order.paymentStatus === 'completed' ? 'text-green-600' : 'text-yellow-600'
            }`}>
              {order.paymentStatus === 'completed' ? '✓ Paid' : '⏳ Pending Payment'}
            </p>
          </div>
        </div>

        {/* Status and Actions */}
        <div className="mt-4 flex items-center justify-between">
          <select
            value={order.status}
            onChange={(e) => onStatusUpdate(order.uuid, e.target.value)}
            className={`px-3 py-1 rounded-lg text-sm font-medium border ${statusColors[order.status]}`}
          >
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="preparing">Preparing</option>
            <option value="out_for_delivery">Out for Delivery</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            {isExpanded ? 'Hide Details' : 'Show Details'}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-4 bg-white space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded">
              <h4 className="font-semibold text-gray-900 mb-2">📍 Delivery Location</h4>
              <p className="text-sm text-gray-700 font-medium">{order.deliveryAddress}</p>
              {order.deliveryLandmark && (
                <p className="text-xs text-gray-600 mt-1">📌 {order.deliveryLandmark}</p>
              )}
              <p className="text-xs text-gray-600 mt-2">
                {order.deliveryCity}, {order.deliveryState} - <span className="font-semibold">{order.deliveryPincode}</span>
              </p>
              {order.deliveryLatitude && order.deliveryLongitude && (
                <p className="text-xs text-blue-600 mt-2">
                  📌 {parseFloat(order.deliveryLatitude).toFixed(4)}, {parseFloat(order.deliveryLongitude).toFixed(4)}
                </p>
              )}
            </div>

            <div className="p-3 bg-gray-50 rounded">
              <h4 className="font-semibold text-gray-900 mb-2">📋 Order Info</h4>
              <div className="text-xs text-gray-600 space-y-1">
                <p>🗓️ Delivery: {new Date(order.createdAt).toLocaleString()}</p>
                <p>⏰ Time Slot: <span className="font-semibold capitalize">{order.deliveryTimeSlot}</span></p>
                {order.orderType === 'subscription' && (
                  <>
                    <p className="mt-2 pt-2 border-t">📋 Plan: <span className="font-semibold">{order.subscriptionPlanName}</span></p>
                    {order.companyName && <p>🏢 Company: <span className="font-semibold">{order.companyName}</span></p>}
                  </>
                )}
              </div>
            </div>
          </div>

          {order.specialInstructions && (
            <div className="p-3 bg-yellow-50 rounded border border-yellow-200">
              <p className="text-xs text-gray-700">
                <span className="font-semibold text-yellow-700">⚠️ Special Instructions:</span>
                <br className="mt-1" />
                {order.specialInstructions}
              </p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <p className="text-xs text-blue-800">
              <span className="font-semibold">💡 Grouping Info:</span>
              {canDeliver ? (
                <>
                  <br />✓ This order is ready to deliver
                  <br />Can be grouped with other <span className="capitalize">{order.deliveryTimeSlot}</span> slot orders to same area ({order.deliveryPincode})
                </>
              ) : (
                <>
                  <br />⚠️ Order needs to be confirmed/prepared before delivery
                </>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Grouped Orders List Component
const GroupedOrdersList = ({ orders, groupBy, onStatusUpdate, statusColors }) => {
  const getGroupKey = (order) => {
    switch (groupBy) {
      case 'type':
        return order.orderType === 'subscription' ? '📦 Subscription Orders' : '🛒 Normal Orders';
      case 'pincode':
        return `${order.deliveryPincode} (${order.deliveryCity})`;
      case 'timeSlot':
        return `${order.deliveryTimeSlot.charAt(0).toUpperCase() + order.deliveryTimeSlot.slice(1)} Slot`;
      case 'status':
        const statusLabel = {
          pending: '⏳ Pending',
          confirmed: '✓ Confirmed',
          preparing: '👨‍🍳 Preparing',
          out_for_delivery: '🚚 Out for Delivery',
          delivered: '✔️ Delivered',
          cancelled: '❌ Cancelled'
        };
        return statusLabel[order.status] || order.status;
      default:
        return 'All Orders';
    }
  };

  const grouped = orders.reduce((acc, order) => {
    const key = getGroupKey(order);
    if (!acc[key]) acc[key] = [];
    acc[key].push(order);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([groupKey, groupOrders]) => (
        <div key={groupKey} className="border border-gray-300 rounded-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 flex justify-between items-center">
            <h3 className="text-lg font-semibold">{groupKey}</h3>
            <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-bold">
              {groupOrders.length} order{groupOrders.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="p-4 space-y-3 bg-gray-50">
            {groupOrders.map((order) => (
              <OrderCard
                key={order.uuid}
                order={order}
                onStatusUpdate={onStatusUpdate}
                statusColors={statusColors}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TodaysOrders;